import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';

const GapStatusChart = ({ data = [], permits = {}, appliedFilters = {}, assets = [] }) => {
  const navigate = useNavigate();

  const canShowData =
    permits.isSuperuser || (permits.hasAnyRole && permits.canGapsRead);

  const desiredKeys = ['Pendiente', 'Iniciado', 'Finalizado'];

  const mockData = {
    Pendiente: { items: [{ 'Ausencia de Control': 8 }, { 'Falta de Documentación': 2 }], total: 10 },
    Iniciado: { items: [{ 'Ausencia de Control': 3 }, { 'Falta de Documentación': 1 }], total: 4 },
    Finalizado: { items: [{ Cerrado: 2 }], total: 2 },
  };

  const chartData = canShowData ? data : mockData;

  const gapTypesSet = new Set();
  desiredKeys.forEach(status => {
    const items = chartData[status]?.items || [];
    items.forEach(item => {
      const type = Object.keys(item)[0];
      gapTypesSet.add(type);
    });
  });

  const gapTypes = Array.from(gapTypesSet);

  const series = useMemo(() => {
    return gapTypes.map(type => {
      const dataByStatus = desiredKeys.map(status => {
        const items = chartData[status]?.items || [];
        const foundItem = items.find(item => Object.keys(item)[0] === type);
        return foundItem ? Object.values(foundItem)[0] : 0;
      });

      return {
        name: type,
        data: dataByStatus,
      };
    });
  }, [gapTypes, desiredKeys, chartData]);

  const maxValue = useMemo(() => {
    const allValues = series.flatMap(s => s.data);
    return Math.max(...allValues, 0);
  }, [series]);

  // ✅ Función para construir URL con filtros del Dashboard
  const buildNavigationUrl = (status, gapType) => {
    const params = new URLSearchParams();

    // (encoding automático):
    params.append('status', status);
    params.append('type', gapType);
    
    // Agregar filtros de ACTIVOS del Dashboard
    if (appliedFilters.asset && appliedFilters.asset.length > 0) {
      const assetNames = appliedFilters.asset
        .map(code => assets.find(a => a.code === code)?.name)
        .filter(Boolean);
      
      if (assetNames.length > 0) {
        params.append('assets', assetNames.join(','));
      }
    }
    
    // Agregar filtros de ÁREAS del Dashboard
    if (appliedFilters.area && appliedFilters.area.length > 0) {
      const allFilterAreas = assets.reduce((acc, asset) => {
        if (asset.filter_areas) {
          asset.filter_areas.forEach(area => {
            if (!acc.find(a => a.id === area.id)) {
              acc.push(area);
            }
          });
        }
        return acc;
      }, []);

      const areaNames = appliedFilters.area
        .map(id => allFilterAreas.find(a => a.id === id)?.name)
        .filter(Boolean);
      
      if (areaNames.length > 0) {
        params.append('areas', areaNames.join(','));
      }
    }
    
    return `/gaps?${params.toString()}`;
  };

  const handleClick = (event, chartContext, config) => {
    if (!canShowData) return;
    if (!chartContext || config === undefined) return;

    const statusIndex = config.dataPointIndex;
    const seriesIndex = config.seriesIndex;

    if (typeof statusIndex === 'number' && typeof seriesIndex === 'number') {
      const status = desiredKeys[statusIndex];
      const gapType = gapTypes[seriesIndex];

      navigate(buildNavigationUrl(status, gapType));
    }
  };

  const options = useMemo(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
      events: {
        dataPointSelection: handleClick,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 4,
      },
    },
    xaxis: {
      categories: desiredKeys,
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxValue),
      forceNiceScale: true,
      labels: {
        formatter: (val) => val.toFixed(0),
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      markers: { width: 10, height: 10 },
      itemMargin: { horizontal: 8, vertical: 0 },
    },
    dataLabels: { enabled: true },
    colors: canShowData
      ? ["#F39C12", "#2ECC71", "#9B59B6"]
      : ['#CCCCCC', '#AAAAAA', '#AAAAAA', '#888888', '#777777'],
    tooltip: { y: { formatter: (val) => val ?? 0 } },
  }), [desiredKeys, canShowData, maxValue, appliedFilters, assets]); // ✅ Dependencias actualizadas

  return (
    <div style={{ position: 'relative', cursor: canShowData ? 'default' : 'not-allowed' }}>
      <Chart options={options} series={series} type="bar" height={300} />
      {!canShowData && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 'bold',
            color: '#555',
          }}
        >
          No tienes permisos para ver este gráfico
        </div>
      )}
    </div>
  );
};

export default GapStatusChart;